import { createFocusTrap } from 'focus-trap';
import { loadConfiguration, OPTIN_STATUS, SCRIPT_NAME, STORAGE_KEY, storageAdapter } from './public';
import { cookieStorageAdapter, localStorageAdapter } from './storage';
import { mergeDeep } from './util';
let idIndex = 0;
/**
 * Create a new unique ID to reference elements
 */
const elementId = () => `toie${++idIndex}`;
export const VERSION_STORAGE_KEY = '__trackingOptInVersion';
const clsx = (...classNames) => classNames
    .filter(c => typeof c === 'string' && c.trim().length > 0)
    .map(c => c.trim())
    .join(' ');
const flattenChildren = (children) => {
    const result = [];
    children.forEach(c => {
        if (Array.isArray(c)) {
            result.push(...flattenChildren(c));
        }
        else {
            result.push(c);
        }
    });
    return result;
};
/**
 * Creates a new {@link Element}
 * @param tagName Name of an element to create through the DOM or a function
 *      creating an Element.
 * @param props Options for the element. If the element is an HTML element, the
 *      props will be set with their getter, if possible. If not, they will be
 *      set as attribute with the specified value.
 *
 *      Props prefixed with `on` will be applied as event handlers, e.g.
 *      `onClick` will be attached as `click` event listener.
 *
 *      If the `tagName` is a function, the `props` are passed as argument.
 * @param compatChildren Children to append to the element. If the `tagName` is a
 *      function, the `children` will be passed as a member `children` in the
 *      first argument to the function.
 *      For compatibility with a jsx factory, children can be passed as separate
 *      arguments as well.
 * @returns The {@link Element} created
 */
const createElement = (tagName, props = {}, ...children) => {
    let el;
    children = flattenChildren(children);
    if (typeof tagName === 'function') {
        el = tagName({ ...props, children });
    }
    else {
        el = document.createElement(tagName);
        Object.keys(props || {}).forEach(k => {
            if (props[k] === null) {
                return;
            }
            if (k.substr(0, 2) === 'on') {
                el.addEventListener(k.substr(2).toLowerCase(), props[k]);
                return;
            }
            el[k] !== undefined ? el[k] = props[k] : el.setAttribute(k, '' + props[k]);
        });
        children.forEach(c => c && el.appendChild(typeof c === 'object' ? c : document.createTextNode('' + c)));
    }
    return el;
};
/**
 * Filter an object by its entries.
 * @param obj The dictionary to filter
 * @param predicate The predicate to evalute the dictionary entries
 * @returns A new object with only the filtered entries
 */
const filterDict = (obj, predicate) => Object.keys(obj).reduce((c, k) => predicate(obj[k]) ? (c[k] = obj[k], c) : c, {});
class PopupWindow {
    constructor(isLegacy) {
        this.element = null;
        this.isLegacy = isLegacy;
    }
    addElement(element) {
        this.element = element;
        this.trap = this.isLegacy && createFocusTrap(element);
    }
    open() {
        if (this.isLegacy) {
            this.element.classList.add('visible');
            this.trap.activate();
        }
        else if (this.element instanceof HTMLDialogElement) {
            this.element.showModal();
        }
    }
    close() {
        if (this.element) {
            if (this.isLegacy) {
                this.element.classList.remove('visible');
                this.trap.deactivate();
            }
            else if (this.element instanceof HTMLDialogElement) {
                this.element.close();
            }
        }
    }
    destroy() {
        if (this.element) {
            this.element.parentNode.removeChild(this.element);
            this.element = null;
        }
    }
    isVisible() {
        return this.isLegacy && this.element
            ? this.element.classList.contains('visible')
            : this.element.hasAttribute('open');
    }
}
export const consentOptIn = (config) => {
    let _config = config;
    const popupWindow = new PopupWindow(!window.HTMLDialogElement);
    const buttonClass = _config.buttonClass ?? 'button';
    if (_config.storage && _config.storage.type === 'cookie') {
        storageAdapter.instance = cookieStorageAdapter(_config.storage.options || {});
    }
    else {
        storageAdapter.instance = localStorageAdapter();
    }
    const listeners = {};
    const ServiceButtons = new Map();
    const registerListener = (service, listener) => {
        if (!(service in listeners)) {
            listeners[service] = [];
        }
        listeners[service].push(listener);
    };
    const removeListener = (service, listener) => {
        if (!(service in listeners)) {
            return;
        }
        listeners[service] = listeners[service].filter(l => l !== listener);
    };
    const enableScriptTags = (serviceId) => (status) => {
        if (status === OPTIN_STATUS.GRANTED) {
            const scriptTags = document.querySelectorAll(`script[data-service="${serviceId}"]`);
            for (let i = 0; i < scriptTags.length; i++) {
                const tag = scriptTags[i];
                const newTag = document.createElement('script');
                for (let a = 0; a < tag.attributes.length; a++) {
                    const attr = tag.attributes[a];
                    if (attr.name === 'type') {
                        continue;
                    }
                    newTag.setAttribute(attr.name, attr.value);
                }
                newTag.innerHTML = tag.innerHTML;
                tag.parentNode.insertBefore(newTag, tag);
                tag.parentNode.removeChild(tag);
            }
        }
    };
    const enableIframes = (serviceId) => (status) => {
        if (status === OPTIN_STATUS.GRANTED) {
            const iframes = document.querySelectorAll(`iframe[data-service=${serviceId}]`);
            for (let i = 0; i < iframes.length; i++) {
                const iframe = iframes[i];
                if (!iframe.hasAttribute('data-src')) {
                    return;
                }
                iframe.src = iframe.getAttribute('data-src');
                iframe.setAttribute('data-invoked', 'true');
                iframe.removeAttribute('data-src');
            }
        }
    };
    let state = loadConfiguration();
    const getServiceStatus = (serviceId) => state[serviceId] || OPTIN_STATUS.UNSPECIFIED;
    const setServiceStatus = (serviceId, status) => {
        state[serviceId] = typeof status === 'boolean'
            ? status
                ? OPTIN_STATUS.GRANTED
                : OPTIN_STATUS.DENIED
            : status;
    };
    const getServiceStatusLabel = (active) => _config.popupLabels[active ? 'enabled' : 'disabled'];
    const updateSwitches = () => {
        ServiceButtons.forEach((serviceId, btn) => {
            if (!btn.isConnected) {
                ServiceButtons.delete(btn);
                return;
            }
            const serviceStatus = getServiceStatus(serviceId);
            let enabled = serviceStatus === OPTIN_STATUS.GRANTED;
            if (serviceStatus === OPTIN_STATUS.UNSPECIFIED) {
                const service = _config.services[serviceId];
                if (service.defaultValue === true) {
                    enabled = true;
                }
                else {
                    enabled = _config.groups[service.group].required === true;
                }
            }
            btn.querySelector('button').setAttribute('aria-checked', '' + enabled);
            btn.querySelector('label').innerHTML = getServiceStatusLabel(enabled);
        });
    };
    /**
     * @param description Description text
     * @param collapsed Whether the description is collapsed by default
     * @param label Label of the description toggle
     * @returns A tuple with the button and the paragraph containing the
     *      description with respective attributes and events for collapsing the
     *      text.
     *
     *      If no description is given, it will return `null` for both.
     */
    const Info = (description, collapsed = false, label = 'info') => {
        if (!description) {
            return [null, null];
        }
        const id = elementId();
        const button = (createElement("button", { type: "button", className: "tracking-optin__info-button", "aria-expanded": collapsed ? 'false' : 'true', "aria-controls": id, "aria-label": label, onClick: (e) => e.target.setAttribute('aria-expanded', e.target.getAttribute('aria-expanded') === 'false' ? 'true' : 'false') }, "i"));
        const text = (createElement("p", { className: "tracking-optin__info-text copy", id: id, innerHTML: description }));
        return [button, text];
    };
    /**
     * Creates a switch component with relevant properties
     * @param props
     */
    const SwitchButton = (props) => {
        let active = props.active;
        const switchId = elementId();
        const statusLabelId = elementId();
        const updateStatusLabel = () => {
            const el = document.getElementById(statusLabelId);
            el.innerText = getServiceStatusLabel(active);
        };
        const onChange = (e) => {
            active = props.onClick(e);
            updateStatusLabel();
        };
        return (createElement("div", { className: "tracking-optin__switch__container" },
            createElement("button", { role: "switch", id: switchId, type: "button", "aria-checked": active ? 'true' : 'false', "aria-labelledby": props.labelledBy, "aria-describedby": props.describedBy, className: "tracking-optin__switch", disabled: props.disabled, onClick: onChange }),
            createElement("label", { id: statusLabelId, className: "tracking-optin__switch__status", htmlFor: switchId }, getServiceStatusLabel(active))));
    };
    /**
     * Creates a service with description and necessary controls based on the
     * {@link TrackingOptInOptions} for a specific ID.
     * @param props
     */
    const Service = (props) => {
        const [infoButton, infoText] = Info(props.service.description, props.service.descriptionCollapsed !== false, `info: ${props.service.label}`);
        const infoTextId = infoText ? infoText.id : null;
        const titleId = elementId();
        const serviceEnabled = ({
            [OPTIN_STATUS.UNSPECIFIED]: props.group.required || props.service.defaultValue === true,
            [OPTIN_STATUS.GRANTED]: true,
            [OPTIN_STATUS.DENIED]: false,
        })[getServiceStatus(props.serviceId)];
        const handleServiceChange = (e) => {
            const v = e.currentTarget.getAttribute('aria-checked') === 'false';
            e.currentTarget.setAttribute('aria-checked', '' + v);
            setServiceStatus(props.serviceId, v);
            return v;
        };
        const btn = (createElement(SwitchButton, { active: serviceEnabled, labelledBy: titleId, describedBy: infoTextId, disabled: props.group.required || null, onClick: handleServiceChange }));
        ServiceButtons.set(btn, props.serviceId);
        return (createElement("li", { className: "tracking-optin__service" },
            createElement("h3", { id: titleId }, props.service.label),
            infoButton,
            infoText,
            btn));
    };
    /**
     * Creates a service-group for a specific group ID based on the given
     * {@link TrackingOptInOptions}.
     * @param props
     */
    const Group = (props) => {
        const services = filterDict(_config.services, s => s.group === props.groupId);
        const [infoButton, infoText] = Info(props.group.description);
        return (createElement("li", { className: "tracking-optin__group" },
            createElement("h2", null, props.group.label),
            infoButton,
            infoText,
            createElement("ul", null, Object.keys(services).map(serviceId => (createElement(Service, { serviceId: serviceId, service: _config.services[serviceId], group: props.group }))))));
    };
    /**
     * Internal helper used when a service is updated
     * @param serviceId ID of the service that changed
     * @param status The new status of the service
     */
    const handleServiceChange = (serviceId, status) => {
        if (listeners[serviceId]) {
            listeners[serviceId].forEach(cb => cb(status));
        }
    };
    /**
     * Updates the configuration and triggers the necessary events
     * @param e
     */
    const handleSave = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        const currentState = loadConfiguration();
        Object.keys(_config.services).forEach(serviceId => {
            const service = _config.services[serviceId];
            const group = _config.groups[service.group];
            const value = group && group.required ? true : (state[serviceId] !== undefined ? state[serviceId] : (service.defaultValue || false));
            state[serviceId] = typeof value === 'boolean' ? (value ? OPTIN_STATUS.GRANTED : OPTIN_STATUS.DENIED) : value;
            if (currentState[serviceId] !== state[serviceId]) {
                handleServiceChange(serviceId, state[serviceId]);
            }
        });
        if (_config.version) {
            storageAdapter.instance.set(VERSION_STORAGE_KEY, _config.version);
        }
        storageAdapter.instance.set(STORAGE_KEY, JSON.stringify(state));
        popupWindow.close();
    };
    /**
     * Updates the configuration and triggers the necessary events
     * @param e
     */
    const handlePreset = (preset) => (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        let serviceIds = [];
        if (preset.services === '*') {
            serviceIds = Object.keys(_config.services);
        }
        else if (typeof preset.services === 'function') {
            const cb = preset.services;
            serviceIds = Object.keys(_config.services).filter(serviceId => {
                return cb(_config.services[serviceId], serviceId);
            });
        }
        else {
            serviceIds = preset.services;
        }
        serviceIds.forEach(serviceId => {
            setServiceStatus(serviceId, OPTIN_STATUS.GRANTED);
        });
        handleSave(e);
    };
    /**
     * Handle escape key press, keep banner open
     * @param ev HTMLKeyEvent
     */
    const handleEscapeKeypress = (ev) => {
        if (ev.keyCode === 27 && popupWindow.isVisible()) {
            ev.preventDefault();
        }
    };
    /**
     * Creates and mounts the popup window based on the
     * {@link TrackingOptInOptions}.
    */
    const buildWindow = () => {
        window.addEventListener('keydown', handleEscapeKeypress);
        if (popupWindow && popupWindow.element !== null) {
            return;
        }
        const frag = document.createDocumentFragment();
        const allServices = Object.keys(_config.services).map(k => _config.services[k]);
        const popupTitleId = 'mscr-consent-ui-title';
        const PopupHtmlTag = popupWindow.isLegacy ? 'div' : 'dialog';
        const popupAttributes = popupWindow.isLegacy
            ? {
                'role': 'dialog',
                'aria-modal': 'true',
                'aria-labelledby': popupTitleId,
                'className': 'tracking-optin__popup tracking-optin__popup--legacy',
            }
            : {
                'aria-labelledby': popupTitleId,
                'className': 'tracking-optin__popup',
            };
        const popup = (createElement(PopupHtmlTag, { ...popupAttributes },
            createElement("form", { onSubmit: handleSave, class: "tracking-optin__form" },
                createElement("h1", { id: popupTitleId }, _config.popupLabels.title),
                createElement("p", { className: "copy", innerHTML: _config.popupLabels.description }),
                createElement("ul", null, Object.keys(_config.groups)
                    .filter(group => allServices.some(s => s.group === group))
                    .map(group => createElement(Group, { groupId: group, group: _config.groups[group] }))),
                createElement("div", { class: "tracking-optin__form__actions" },
                    createElement("button", { className: clsx(buttonClass, buttonClass + '--primary', 'tracking-optin__button', 'tracking-optin__button--primary'), onClick: handleSave }, _config.popupLabels.save),
                    (_config.enablePresets || []).map(preset => (createElement("button", { className: clsx(buttonClass, buttonClass + '--primary', 'tracking-optin__button', preset.key && buttonClass + '--' + preset.key), onClick: handlePreset(preset) }, preset.label)))))));
        frag.appendChild(popup);
        document.body.insertBefore(frag, document.body.firstChild);
        popupWindow.addElement(document.querySelector('.tracking-optin__popup'));
    };
    /**
     * Resets the stored consent options
     */
    const reset = () => {
        popupWindow.destroy();
        window.removeEventListener('keydown', handleEscapeKeypress);
        Object.keys(loadConfiguration()).forEach(serviceId => {
            handleServiceChange(serviceId, OPTIN_STATUS.UNSPECIFIED);
        });
        storageAdapter.instance.remove(STORAGE_KEY);
        state = {};
    };
    /**
     * Displays the consent banner. If necessary, the banner is built and
     * mounted.
     */
    const showOptions = () => {
        if (_config.disablePopup) {
            return;
        }
        buildWindow();
        updateSwitches();
        popupWindow.open();
    };
    const clearHash = () => {
        history.replaceState('', document.title, window.location.pathname + window.location.search);
    };
    const setOptions = (config) => {
        const wasVisible = popupWindow.isVisible();
        _config = mergeDeep(_config, config);
        popupWindow.destroy();
        if (wasVisible) {
            showOptions();
        }
    };
    /**
     * Setup the element
     */
    const init = () => {
        Object.keys(_config.services).forEach(serviceId => {
            registerListener(serviceId, enableScriptTags(serviceId));
            registerListener(serviceId, enableIframes(serviceId));
            handleServiceChange(serviceId, state[serviceId] || OPTIN_STATUS.UNSPECIFIED);
        });
        const handleHash = () => {
            if (document.location.hash === '#consentOptions') {
                showOptions();
                clearHash();
            }
            else if (document.location.hash === '#resetConsentOptions') {
                reset();
                showOptions();
                clearHash();
            }
        };
        window.addEventListener('hashchange', handleHash);
        handleHash();
    };
    return {
        on: registerListener,
        off: removeListener,
        getServiceStatus,
        setServiceStatus: (service, status) => {
            setServiceStatus(service, status);
            handleSave();
        },
        showOptions,
        setOptions,
        reset,
        init,
    };
};
export const settingsExist = (version) => storageAdapter.instance.get(STORAGE_KEY) !== null
    && (!version || storageAdapter.instance.get(VERSION_STORAGE_KEY) === version);
const INITIALIZED = '__isinit';
export const initConsentUi = () => {
    if (window[SCRIPT_NAME] && !window[SCRIPT_NAME][INITIALIZED]) {
        const config = window[SCRIPT_NAME].c;
        if (!config || config.enabled === false) {
            return;
        }
        const listeners = window[SCRIPT_NAME].l;
        const optIn = consentOptIn(config);
        window[SCRIPT_NAME] = optIn;
        window[SCRIPT_NAME][INITIALIZED] = true;
        listeners.map(([s, c]) => optIn.on(s, c));
        optIn.init();
        if (!settingsExist(config.version)) {
            optIn.showOptions();
        }
    }
};
